import React from "react";
import {
    Building2,
    Package,
    LogOut,
    CheckCircle,
    Clock,
    Users,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../shared/ui/LanguageSwitcher";

const ControlPanelView = ({
    authData,
    logout,
    statistics,
    companies,
    loading,
    setViewingCompany,
    setShowProductManagement,
}) => {
    const { t } = useTranslation();

    const stats = [
        {
            label: t("admin.stats.totalCompanies"),
            value: statistics?.totalCompanies,
            icon: Building2,
            color: "text-pink-300",
        },
        {
            label: t("admin.stats.paidCompanies"),
            value: statistics?.paidCompanies,
            icon: CheckCircle,
            color: "text-green-300",
        },
        {
            label: t("admin.stats.activeCompanies"),
            value: statistics?.activeCompanies,
            icon: CheckCircle,
            color: "text-teal-300",
        },
        {
            label: t("admin.stats.pendingCompanies"),
            value: statistics?.pendingCompanies,
            icon: Clock,
            color: "text-yellow-300",
        },
        {
            label: t("admin.stats.totalUsers"),
            value: statistics?.totalUsers,
            icon: Users,
            color: "text-purple-300",
        },
        {
            label: t("admin.stats.totalProducts"),
            value: statistics?.totalProducts,
            icon: Package,
            color: "text-indigo-300",
        },
    ];

    const tableHeaders = [
        t("admin.table.company"),
        t("admin.table.manager"),
        t("admin.table.phone"),
        t("admin.table.products"),
        t("admin.table.status"),
        t("admin.table.actions"),
    ];

    return (
        <div className="relative min-h-screen text-white overflow-hidden">
            <video
                autoPlay
                loop
                muted
                playsInline
                className="absolute inset-0 w-full h-full object-cover"
            >
                <source src="/login-bg.mp4" type="video/mp4" />
            </video>

            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-purple-900/40 to-black/80"></div>

            <div className="relative z-10">
                <nav className="backdrop-blur-xl bg-white/10 border-b border-white/20 shadow-[0_0_25px_rgba(255,0,255,0.25)]">
                    <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
                        <div className="flex items-center gap-4">
                            <Building2 className="w-8 h-8 text-pink-300 drop-shadow-lg" />
                            <div>
                                <h1 className="text-xl font-bold">
                                    {t("admin.controlPanelTitle")}
                                </h1>
                                <p className="text-sm text-gray-200">
                                    {authData?.fullName} - {authData?.role}
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            <LanguageSwitcher />

                            {authData?.role === "Admin" && (
                                <button
                                    onClick={() => setShowProductManagement(true)}
                                    className="px-4 py-2 rounded-xl bg-purple-600/80 hover:bg-purple-700 
                                           text-white flex items-center gap-2 backdrop-blur-lg"
                                >
                                    <Package className="w-5 h-5" />
                                    {t("admin.manageProducts")}
                                </button>
                            )}

                            <button
                                onClick={logout}
                                className="w-full py-3 px-2 rounded-xl text-white font-semibold 
               bg-gradient-to-r from-pink-500 to-red-500 
               hover:opacity-90 transition disabled:opacity-50
               flex items-center justify-center gap-2"
                            >
                                <LogOut className="w-5 h-5" />
                                <span>{t("common.logout")}</span>
                            </button>
                        </div>
                    </div>
                </nav>

                <div className="max-w-7xl mx-auto p-6 pt-10">
                    {statistics && (
                        <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4 mb-10">
                            {stats.map((item, i) => (
                                <div
                                    key={i}
                                    className="backdrop-blur-xl bg-white/10 rounded-xl p-4 
                                           shadow-[0_0_25px_rgba(255,0,255,0.15)] border border-white/20"
                                >
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <p className="text-gray-200 text-xs">
                                                {item.label}
                                            </p>
                                            <p className={`text-2xl font-bold ${item.color}`}>
                                                {item.value}
                                            </p>
                                        </div>
                                        <item.icon
                                            className={`w-8 h-8 opacity-60 ${item.color}`}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="backdrop-blur-xl bg-white/10 rounded-2xl shadow-[0_0_40px_rgba(255,0,255,0.20)] border border-white/20 overflow-hidden">
                        <div className="p-6 bg-gradient-to-r from-pink-500/20 to-purple-600/30 backdrop-blur-xl">
                            <h2 className="text-xl font-bold">
                                {t("admin.companyTableTitle")}
                            </h2>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full text-white">
                                <thead className="bg-white/10">
                                    <tr>
                                        {tableHeaders.map((header) => (
                                            <th
                                                key={header}
                                                className="px-4 py-3 text-right text-sm font-semibold"
                                            >
                                                {header}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>

                                <tbody className="divide-y divide-white/10">
                                    {companies.map((company) => (
                                        <tr
                                            key={company.id}
                                            className="hover:bg-white/10 transition"
                                        >
                                            <td className="px-4 py-3">
                                                <div className="font-semibold">
                                                    {company.companyName}
                                                </div>
                                                <div className="text-xs text-gray-300">
                                                    {company.companyNumber}
                                                </div>
                                            </td>

                                            <td className="px-4 py-3">
                                                <div>{company.managerName}</div>
                                                <div className="text-xs text-gray-300">
                                                    {company.email}
                                                </div>
                                            </td>

                                            <td className="px-4 py-3">
                                                {company.phoneNumber}
                                            </td>

                                            <td className="px-4 py-3">
                                                <div className="flex flex-wrap gap-1">
                                                    {company.products
                                                        .slice(0, 3)
                                                        .map((p) => (
                                                            <span
                                                                key={p.productId}
                                                                className={`px-2 py-1 rounded text-xs 
                                                        ${
                                                            p.hasAccess
                                                                ? "bg-green-500/20 text-green-300"
                                                                : "bg-gray-500/20 text-gray-300"
                                                        }`}
                                                            >
                                                                {p.productName}
                                                            </span>
                                                        ))}

                                                    {company.products.length > 3 && (
                                                        <span className="px-2 py-1 rounded bg-gray-500/20 text-gray-300 text-xs">
                                                            +{company.products.length - 3}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>

                                            <td className="px-4 py-3">
                                                {company.hasPaid ? (
                                                    <span className="text-green-300 flex items-center gap-1 text-xs">
                                                        <CheckCircle className="w-4 h-4" />
                                                        {t("common.statusPaid")}
                                                    </span>
                                                ) : (
                                                    <span className="text-yellow-300 flex items-center gap-1 text-xs">
                                                        <Clock className="w-4 h-4" />
                                                        {t("common.statusPending")}
                                                    </span>
                                                )}
                                            </td>

                                            <td className="px-4 py-3">
                                                <button
                                                    onClick={() =>
                                                        setViewingCompany(company)
                                                    }
                                                    className="px-3 py-1.5 rounded-lg bg-pink-600/80 hover:bg-pink-700 
                                                           text-white text-xs backdrop-blur-lg"
                                                >
                                                    {t("admin.viewDetails")}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ControlPanelView;
